import { MessageCircle, LifeBuoy, FileText, Activity, CircleHelp } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader } from "@/components/PageHeader";
import { GlassCard } from "@/components/GlassCard";
import { Reveal } from "@/components/Reveal";
import { SUPPORT_SERVER_URL } from "@/config";

const EXPECTATIONS = [
  { icon: MessageCircle, title: "Real humans", text: "The Xevon team and an active community answer questions directly." },
  { icon: LifeBuoy, title: "Setup help", text: "Stuck on configuration? Get walkthroughs for any module." },
  { icon: CircleHelp, title: "Bug reports", text: "Report issues and track known problems with the team." },
];

const QUICK_LINKS = [
  { to: "/faq", icon: CircleHelp, label: "Read the FAQ", testId: "support-link-faq" },
  { to: "/docs", icon: FileText, label: "Browse the docs", testId: "support-link-docs" },
  { to: "/status", icon: Activity, label: "Check live status", testId: "support-link-status" },
];

export default function Support() {
  useSEO({
    title: "Support",
    description: "Get help with Xevon — join the official support server for setup help, bug reports, and questions.",
    path: "/support",
  });

  return (
    <>
      <PageHeader
        kicker="Support"
        title="We're here to help."
        description="The fastest way to get help is the official Xevon support server — real people, real answers."
      />
      <section className="mx-auto w-full max-w-7xl px-6 pb-32" data-testid="support-page">
        <Reveal>
          <GlassCard className="relative overflow-hidden p-10 text-center md:p-16" hover={false}>
            <div
              className="pointer-events-none absolute inset-0"
              aria-hidden="true"
              style={{ background: "radial-gradient(ellipse 50% 60% at 50% 0%, rgba(255,255,255,0.06), transparent 70%)" }}
            />
            <h2 className="font-display text-2xl font-semibold tracking-tight text-paper-100 md:text-4xl">
              Join the Xevon support server
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-paper-400 md:text-base">
              Setup questions, bug reports, feature requests, and Premium updates — all in one place.
            </p>
            <a
              href={SUPPORT_SERVER_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="support-join-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.04]"
            >
              Join the Support Server
            </a>
          </GlassCard>
        </Reveal>

        <div className="mt-16 grid gap-4 md:grid-cols-3">
          {EXPECTATIONS.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.06}>
              <GlassCard className="h-full p-8" data-testid={`support-expectation-${i}`}>
                <item.icon className="h-6 w-6 text-paper-100" aria-hidden="true" />
                <h3 className="mt-5 font-display text-lg font-semibold text-paper-100">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-paper-400">{item.text}</p>
              </GlassCard>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-16">
            <h3 className="font-display text-xl font-semibold text-paper-100">Before you ask</h3>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              {QUICK_LINKS.map((link) => (
                <Link key={link.to} to={link.to} data-testid={link.testId}>
                  <GlassCard className="group flex h-full items-center gap-4 p-6">
                    <link.icon className="h-5 w-5 shrink-0 text-paper-400 transition-colors duration-300 group-hover:text-paper-100" aria-hidden="true" />
                    <span className="text-sm font-semibold text-paper-200 transition-colors duration-300 group-hover:text-paper-100">
                      {link.label}
                    </span>
                  </GlassCard>
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
